/**
 * Offline Status Indicator
 *
 * Shows current connectivity status and pending sync items
 */

import { Wifi, WifiOff, RefreshCw, CloudOff, Download } from 'lucide-react';
import { useOnlineStatus, usePendingSync, useInstallPrompt } from '../../hooks/useOffline';

export function OfflineIndicator() {
  const isOnline = useOnlineStatus();
  const { pendingCount, isSyncing, triggerSync } = usePendingSync();

  if (isOnline && pendingCount === 0) {
    return null; // Don't show anything when everything is normal
  }

  return (
    <div className="fixed bottom-4 left-4 z-50">
      <div
        className={`flex items-center gap-2 px-4 py-2 rounded-xl shadow-lg text-sm font-medium transition-all ${
          isOnline
            ? 'bg-primary text-white'
            : 'bg-amber-500 text-white'
        }`}
      >
        {isOnline ? (
          <>
            {isSyncing ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                <span>Syncing...</span>
              </>
            ) : (
              <>
                <CloudOff className="w-4 h-4" />
                <span>{pendingCount} pending</span>
                <button
                  onClick={triggerSync}
                  className="ml-2 px-2 py-1 bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
                >
                  Sync now
                </button>
              </>
            )}
          </>
        ) : (
          <>
            <WifiOff className="w-4 h-4" />
            <span>Offline</span>
            {pendingCount > 0 && (
              <span className="ml-1 px-2 py-0.5 bg-white/20 rounded-full text-xs">
                {pendingCount} queued
              </span>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export function OfflineBanner() {
  const isOnline = useOnlineStatus();

  if (isOnline) return null;

  return (
    <div className="bg-amber-500 text-white px-4 py-2 text-center text-sm">
      <div className="flex items-center justify-center gap-2">
        <WifiOff className="w-4 h-4" />
        <span>
          You're offline. You can still view saved documents and translations.
        </span>
      </div>
    </div>
  );
}

export function InstallPrompt() {
  const { isInstallable, promptInstall } = useInstallPrompt();

  if (!isInstallable) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50">
      <div className="bg-navy text-white px-4 py-3 rounded-xl shadow-lg max-w-xs">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center flex-shrink-0">
            <Download className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <p className="font-medium text-sm">Install MedTranslate</p>
            <p className="text-xs text-gray-300 mt-1">
              Add to your home screen for quick access and offline use.
            </p>
            <div className="flex gap-2 mt-3">
              <button
                onClick={promptInstall}
                className="px-3 py-1.5 bg-primary text-white rounded-lg text-xs font-medium hover:bg-primary-dark transition-colors"
              >
                Install
              </button>
              <button
                onClick={() => {}}
                className="px-3 py-1.5 text-gray-300 text-xs hover:text-white transition-colors"
              >
                Not now
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ConnectionStatus({ className = '' }) {
  const isOnline = useOnlineStatus();

  return (
    <div className={`flex items-center gap-1.5 ${className}`}>
      {isOnline ? (
        <>
          <Wifi className="w-4 h-4 text-green-500" />
          <span className="text-xs text-gray-500">Online</span>
        </>
      ) : (
        <>
          <WifiOff className="w-4 h-4 text-amber-500" />
          <span className="text-xs text-amber-600">Offline</span>
        </>
      )}
    </div>
  );
}

export default OfflineIndicator;
